import { DatePipe, DecimalPipe } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
} from "@angular/core";
import { Router } from "@angular/router";
import Highcharts from "highcharts";
import dataJson from "../../../json/re_dsm_meter_data.json";
import {
  ScheduleUploadRecord,
  ScheduleUploadStoreService,
} from "../../services/schedule-upload-store.service";

type Generator = {
  generator_id: number;
  name: string;
  type: string;
  installed_capacity_mw: number;
  status: string;
};
type Unit = {
  unit_id: number;
  generator_id: number;
  unit_name: string;
  rated_capacity_mw: number;
  available_capacity_mw: number;
};
type Upload = ScheduleUploadRecord;
type Data = { generators: Generator[]; generator_units: Unit[] };

@Component({
  selector: "app-schedule-dashboard",
  standalone: true,
  imports: [DatePipe, DecimalPipe],
  templateUrl: "./schedule-dashboard.component.html",
  styleUrls: ["./schedule-dashboard.component.scss"],
})
export class ScheduleDashboardComponent implements AfterViewInit, OnDestroy {
  @ViewChild("generatorChart")
  private generatorChartElement?: ElementRef<HTMLElement>;
  @ViewChild("unitChart") private unitChartElement?: ElementRef<HTMLElement>;
  private readonly data = dataJson as Data;
  private readonly uploadStore = inject(ScheduleUploadStoreService);
  private readonly router = inject(Router);
  readonly generators = this.data.generators
    .filter((g) => g.status === "Operational")
    .slice(0, 5);
  readonly selectedDate = signal(this.today());
  readonly scheduleType = signal("Day Ahead");
  readonly generatorId = signal<number | null>(null);
  readonly unitId = signal<number | null>(null);
  readonly chartView = signal<"both" | "graph" | "table">("both");
  readonly uploads = this.uploadStore.records;
  readonly selectedUpload = computed(
    () =>
      this.uploads().find(
        (record) =>
          record.generatorId === this.generatorId() &&
          record.unitId === this.unitId() &&
          record.scheduleType === this.scheduleType() &&
          record.scheduleDate === this.selectedDate(),
      ) ??
      this.uploads()[0] ??
      null,
  );
  readonly units = computed(() =>
    this.data.generator_units
      .filter((u) =>
        this.generators.some((g) => g.generator_id === u.generator_id),
      )
      .slice(0, 10),
  );
  readonly totalCapacity = computed(() =>
    this.generators.reduce((sum, g) => sum + g.installed_capacity_mw, 0),
  );
  readonly scheduleEnergy = computed(() =>
    this.selectedUpload()
      ? this.selectedUpload()!.scheduleBlocks.reduce(
          (sum, block) => sum + (block.scheduleMw ?? 0) / 4,
          0,
        )
      : this.totalCapacity() * 24 * 0.94,
  );
  readonly actualEnergy = computed(() =>
    this.selectedUpload()
      ? this.selectedUpload()!.scheduleBlocks.reduce(
          (sum, block) => sum + (block.actualCapacityMw ?? 0) / 4,
          0,
        )
      : this.scheduleEnergy() * 0.951,
  );
  readonly avgDeviation = computed(
    () =>
      ((this.actualEnergy() - this.scheduleEnergy()) / this.scheduleEnergy()) *
      100,
  );
  private generatorChart?: Highcharts.Chart;
  private unitChart?: Highcharts.Chart;

  constructor() {
    const latest = this.uploads()[0];
    if (latest) {
      this.selectedDate.set(latest.scheduleDate);
      this.scheduleType.set(latest.scheduleType);
      this.generatorId.set(latest.generatorId);
      this.unitId.set(latest.unitId);
    }
  }

  ngAfterViewInit(): void {
    this.renderCharts();
  }
  ngOnDestroy(): void {
    this.generatorChart?.destroy();
    this.unitChart?.destroy();
  }
  search(): void {
    this.uploadStore.refresh();
    this.renderCharts();
  }
  reset(): void {
    this.selectedDate.set(this.today());
    this.scheduleType.set("Day Ahead");
    this.generatorId.set(null);
    this.unitId.set(null);
    this.renderCharts();
  }
  setView(view: "graph" | "table"): void {
    this.chartView.set(view);
    if (view === "graph") setTimeout(() => this.renderCharts());
  }
  changeGenerator(value: string): void {
    this.generatorId.set(value ? Number(value) : null);
  }
  changeUnit(value: string): void {
    this.unitId.set(value ? Number(value) : null);
  }
  unitsForGenerator(generatorId: number): Unit[] {
    return this.data.generator_units.filter(
      (u) => u.generator_id === generatorId,
    );
  }
  generatorName(id: number): string {
    return this.data.generators.find((g) => g.generator_id === id)?.name || "-";
  }
  unitName(id: number | null): string {
    return id
      ? this.data.generator_units.find((u) => u.unit_id === id)?.unit_name ||
          "-"
      : "All";
  }
  viewSchedule(upload: Upload): void {
    void this.router.navigate(["/schedule-monitoring", upload.id]);
  }
  capacityFor(generator: Generator): number {
    return (
      this.unitsForGenerator(generator.generator_id).reduce(
        (sum, unit) => sum + unit.rated_capacity_mw,
        0,
      ) || generator.installed_capacity_mw
    );
  }
  energy(capacity: number): number {
    return capacity * 24 * 0.94;
  }
  actual(capacity: number): number {
    return this.energy(capacity) * 0.951;
  }
  deviation(capacity: number): number {
    return this.actual(capacity) - this.energy(capacity);
  }
  private renderCharts(): void {
    if (
      !this.generatorChartElement ||
      !this.unitChartElement ||
      this.chartView() === "table"
    )
      return;
    this.generatorChart?.destroy();
    this.unitChart?.destroy();
    this.generatorChart = Highcharts.chart(
      this.generatorChartElement.nativeElement,
      this.chartOptions(
        "Generator-wise Schedule vs Actual (Total MW)",
        this.generators.map((generator, index) => ({
          name: generator.name,
          capacity: this.capacityFor(generator),
          index,
        })),
      ),
    );
    this.unitChart = Highcharts.chart(
      this.unitChartElement.nativeElement,
      this.chartOptions(
        "Unit-wise Schedule vs Actual (MW)",
        this.units()
          .slice(0, 5)
          .map((unit, index) => ({
            name: unit.unit_name,
            capacity: unit.rated_capacity_mw,
            index,
          })),
      ),
    );
  }
  private chartOptions(
    title: string,
    assets: { name: string; capacity: number; index: number }[],
  ): Highcharts.Options {
    const colors = ["#1765f3", "#9a42f3", "#11a866", "#fa812e", "#16aeb3"];
    const series: Highcharts.SeriesOptionsType[] = assets.flatMap(
      (asset) =>
        [
          {
            type: "line",
            name: `${asset.name} (Sch)`,
            data: this.blockValues(asset.capacity, asset.index),
            color: colors[asset.index % colors.length],
            lineWidth: 1.5,
            marker: { enabled: false },
          },
          {
            type: "line",
            name: `${asset.name} (Act)`,
            data: this.blockValues(asset.capacity, asset.index, true),
            color: colors[asset.index % colors.length],
            lineWidth: 1.5,
            dashStyle: "ShortDash",
            marker: { enabled: false },
          },
        ] as Highcharts.SeriesOptionsType[],
    );
    return {
      chart: {
        type: "line",
        height: 210,
        spacing: [10, 8, 4, 8],
        backgroundColor: "transparent",
      },
      title: {
        text: title,
        style: { color: "#173563", fontSize: "12px", fontWeight: "700" },
      },
      xAxis: {
        categories: Array.from({ length: 96 }, (_, index) => String(index + 1)),
        tickInterval: 12,
        title: { text: "Block No. (15-min)", style: { fontSize: "10px" } },
        labels: { style: { fontSize: "9px" } },
        lineColor: "#c8d5e8",
      },
      yAxis: {
        min: 0,
        title: { text: "MW", style: { fontSize: "10px" } },
        gridLineColor: "#e8eef6",
        labels: { style: { fontSize: "9px" } },
      },
      tooltip: { shared: true, valueSuffix: " MW" },
      legend: {
        itemStyle: { fontSize: "9px", fontWeight: "500" },
        symbolWidth: 14,
        margin: 2,
      },
      credits: { enabled: false },
      series,
    };
  }
  private blockValues(
    capacity: number,
    seed: number,
    actual = false,
  ): number[] {
    const upload = this.selectedUpload();
    if (upload && seed === 0)
      return upload.scheduleBlocks.map((block) =>
        actual ? (block.actualCapacityMw ?? 0) : (block.scheduleMw ?? 0),
      );
    return Array.from(
      { length: 96 },
      (_, index) =>
        Math.round(
          capacity *
            (0.76 +
              Math.sin(index / 7 + seed) * 0.07 +
              Math.sin(index / 2.8) * 0.025 -
              (actual ? 0.045 : 0)) *
            100,
        ) / 100,
    );
  }
  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
